import { call, put, select, takeEvery } from "redux-saga/effects";
import { supabase } from "@/lib/supabase";
import {
  fetchModels,
  fetchModelsSuccess,
  fetchModelsFailure,
  changeModelStatus,
  changeModelStatusFailure,
  changeModelStatusSuccess,
} from "./slice";
import { PayloadAction } from "@reduxjs/toolkit";
import { applySagaError } from "../utils/saga";
import { throwOnSupabaseError } from "../../utils/errors";
import { ChangeModelStatusPayload } from "./types";
import { Model } from "../../types/models";
import { selectAuthAccountId } from "../selectors/auth";
import { MODELS_TABLE_NAME, USER_MODELS_TABLE_NAME } from "../const";
import analytics from "@/lib/Analytics";
import { selectModelsState } from "../selectors/models";



export function* fetchModelsSaga() {
  try {
    const userID: string | null = yield select(selectAuthAccountId);

    const { data, error } = yield call(() =>
      supabase
        .from(MODELS_TABLE_NAME)
        .select('*')
    );

    const { data: userModels, error: userModelsError } = yield call(() =>
      supabase
        .from(USER_MODELS_TABLE_NAME)
        .select('is_active, model_id')
        .eq('user_id', userID)
    );

    throwOnSupabaseError(error);
    throwOnSupabaseError(userModelsError);

    const modelsWithStatus = data.map((model: Model) => {
      return {
        ...model,
        deleted_at: model.deleted_at,
        is_active_user: userModels.find((userModel: Model) => userModel.model_id === model.id)?.is_active,
      }
    });

    yield put(fetchModelsSuccess(modelsWithStatus));

  } catch (error) {
    yield* applySagaError(error, {
      operationName: "models.fetchModels",
      failureAction: fetchModelsFailure,
    });
  }
}

export function* changeModelStatusSaga(action: PayloadAction<ChangeModelStatusPayload>) {
  try {
    const { id, status } = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);
    const { error } = yield call(() =>
      supabase
        .from(USER_MODELS_TABLE_NAME)
        .update({ is_active: status })
        .eq('model_id', id)
        .eq('user_id', userID)
    );

    throwOnSupabaseError(error);

    yield put(fetchModels());
    yield put(changeModelStatusSuccess());

    const models: Model[] | null = yield select(selectModelsState);
    const modelName = models?.find((m) => m.id === id)?.name || 'Unknown Model';

    analytics.track('Model Status Changed', {
      model_name: modelName,
      status: status ? 'active' : 'inactive'
    });

  } catch (error) {
    yield* applySagaError(error, {
      operationName: "models.changeModelStatus",
      failureAction: changeModelStatusFailure,
    });
  }
}



export function* modelsSaga() {
  yield takeEvery(fetchModels.type, fetchModelsSaga);
  yield takeEvery(changeModelStatus.type, changeModelStatusSaga);
}